import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { buildDemoAllocations } from './allocationDemoData';
import useStyles from './billDetailsStyles';
import { SESSION_ONLY_HINT } from './billDetailsUtils';
import { withAllocationSetting } from './billEditUtils';
import { ConfirmActionModal } from './ConfirmActionModal';
import { PropertyAllocation } from './PropertyAllocation';
const APPROVE_ALL_CONFIRM_MESSAGE = 'This will approve all pending allocation suggestions for this bill and apply them across all listed ' + 'properties and utilities. Please confirm that the allocation amounts and resident charges have been reviewed.';

/**
 * Allocation tables for every property/utility on the bill (Figma: "Allocation suggestions" card).
 * The tables are derived from the bill, the allocation inputs and the approvals made in this
 * session, so editing an input re-runs the whole calculation on the spot.
 */
export function AllocationSuggestionsCard(_ref) {
  let approvedKeys = _ref.approvedKeys,
    currency = _ref.currency,
    data = _ref.data,
    onApprove = _ref.onApprove,
    onSaveSettings = _ref.onSaveSettings,
    settings = _ref.settings;
  const classes = useStyles();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    draftSettings = _useState2[0],
    setDraftSettings = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isApproveAllConfirmOpen = _useState4[0],
    setIsApproveAllConfirmOpen = _useState4[1];
  const isEditing = draftSettings !== null;
  const activeSettings = isEditing ? draftSettings : settings;
  const allocations = useMemo(() => buildDemoAllocations(data, activeSettings, approvedKeys), [activeSettings, approvedKeys, data]);
  const tables = allocations.flatMap(allocation => allocation.utilities || []);
  const pendingTables = tables.filter(table => !table.isApproved);
  if (!tables.length) return null;
  const handleChangeSetting = (propertyIndex, serviceType, patch) => setDraftSettings(current => withAllocationSetting(current, propertyIndex, serviceType, patch));
  const handleSave = () => {
    onSaveSettings(draftSettings);
    setDraftSettings(null);
  };
  const handleApprove = key => onApprove([...new Set([...(approvedKeys || []), key])]);
  const handleApproveAll = () => {
    setIsApproveAllConfirmOpen(false);
    onApprove(tables.map(table => table.key));
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.card, " ").concat(classes.cardTinted)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeaderTitles
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.cardTitle
  }, isEditing ? 'Edit allocation suggestions' : 'Allocation suggestions'), isEditing ? null : /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(pendingTables.length ? '' : classes.statusChipDone)
  }, pendingTables.length ? "".concat(pendingTables.length, "/").concat(tables.length, " Pending approval") : 'All allocated')), isEditing ? null : /*#__PURE__*/React.createElement("button", {
    "aria-label": "Edit allocations",
    className: classes.iconButton,
    disabled: !onSaveSettings,
    onClick: () => setDraftSettings(settings || {}),
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "edit"
  }))), allocations.map((allocation, index) => /*#__PURE__*/React.createElement(PropertyAllocation, {
    allocation: allocation,
    currency: currency,
    defaultExpanded: index === 0,
    isEditing: isEditing,
    key: allocation.address || index,
    onApprove: onApprove ? handleApprove : undefined,
    onChangeSetting: handleChangeSetting,
    settings: activeSettings
  })), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editFooter
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDraftSettings(null),
    size: "small",
    variant: "outlined"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSave,
    size: "small",
    variant: "contained"
  }, "Save")) : /*#__PURE__*/React.createElement("div", {
    className: classes.cardActions
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: SESSION_ONLY_HINT,
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !onApprove || !pendingTables.length,
    onClick: () => setIsApproveAllConfirmOpen(true),
    size: "small",
    variant: "contained"
  }, pendingTables.length ? 'Approve and allocate all' : 'All allocated'))), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Approve and allocate all",
    isOpen: isApproveAllConfirmOpen,
    message: APPROVE_ALL_CONFIRM_MESSAGE,
    onClose: () => setIsApproveAllConfirmOpen(false),
    onConfirm: handleApproveAll,
    title: "Approve and allocate all charges?"
  })));
}